import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import Scrollbar from 'smooth-scrollbar'
import { ScrollbarOptions } from 'smooth-scrollbar/interfaces'

import { DisableScrollPlugin, NhShapeAnimationPlugin } from '@/lib/smoothScroll'

type SmoothScrollContextProps = {
  defaultElement: string
  getScrollbar: (el: HTMLElement | null) => Scrollbar | undefined
  handleDestroy: (el: HTMLElement) => void
  handleInit: (el: HTMLElement) => Scrollbar
  scrollToTop: () => void
}

type SmoothScrollProviderProps = PropsWithChildren

const SmoothScrollContext = createContext<SmoothScrollContextProps>(
  {} as SmoothScrollContextProps,
)

export const SmoothScrollProvider: React.FC<SmoothScrollProviderProps> = ({
  children,
}) => {
  const defaultElement = 'smooth-scroll'
  const options: Partial<ScrollbarOptions> = useMemo(() => {
    const newOptions = {
      damping: 0.05,
    }
    return newOptions
  }, [])

  const handleDestroy = useCallback((el: HTMLElement) => {
    const scrollbar = Scrollbar.get(el)

    if (scrollbar) {
      scrollbar.destroy()
    }
  }, [])

  const handleInit = useCallback(
    (el: HTMLElement) => {
      Scrollbar.use(NhShapeAnimationPlugin, DisableScrollPlugin)

      const scrollbar = Scrollbar.get(el)
      if (scrollbar) return scrollbar

      const newScrollbar = Scrollbar.init(el, options)
      return newScrollbar
    },
    [options],
  )

  const getScrollbar = useCallback(
    (el: HTMLElement | null): Scrollbar | undefined =>
      el ? handleInit(el) : undefined,
    [handleInit],
  )

  const scrollToTop = useCallback(() => {
    const el = document.getElementById(defaultElement)
    const scrollbar = getScrollbar(el)
    if (!scrollbar) return
    scrollbar.scrollTop = 0
  }, [getScrollbar])

  useEffect(() => {
    const nextApp = document.getElementById(defaultElement)
    if (!nextApp) return

    handleInit(nextApp)
  }, [handleInit])

  const providerProps = useMemo(
    () => ({
      defaultElement,
      getScrollbar,
      handleDestroy,
      handleInit,
      scrollToTop,
    }),
    [defaultElement, getScrollbar, handleDestroy, handleInit, scrollToTop],
  )

  return (
    <SmoothScrollContext.Provider value={providerProps}>
      {children}
    </SmoothScrollContext.Provider>
  )
}

export const useSmoothScroll = () => {
  const context = useContext(SmoothScrollContext)

  if (!context) {
    throw new Error(
      'useSmoothScroll must be used within a SmoothScrollProvider',
    )
  }

  return context
}
